<template>
  <div
    class="w-full flex flex-col outline-none"
    :class="[marginBottom ? 'mb-10' : '']"
  >
    <div
      tabindex="0"
      class="rounded relative bg-white focus:outline-none"
      :class="[
        borderEnabled ? 'border border-truegray-300' : '',
        shadowEnabled ? 'shadow' : '',
        marginBottom ? 'mb-10' : '',
        fixedWidth && !fixedWidthSmall && !fixedWidthLarge
          ? 'w-full lg:w-48'
          : '',
        fixedWidth && fixedWidthSmall ? 'w-full lg:w-32' : '',
        fixedWidth && fixedWidthLarge ? 'w-full lg:w-60' : '',
        height ? `h-${height}` : '',
      ]"
    >
      <!-- Label -->
      <h6
        class="absolute transform duration-100 ease-linear px-1 left-3 top-1 z-20 text-truegray-700"
      >
        {{ label }} <span v-if="required" class="italic">required</span>
      </h6>
      <!-- /Label -->

      <!-- Placeholder -->
      <button
        type="button"
        class="flex-grow px-4 mr-0 w-full outline-none focus:outline-none bg-transparent h-12 font-primary text-black"
        :class="[
          focused ? 'rounded-b-none' : '',
          placeholderBlack ? 'placeholder-black' : 'placeholder-truegray-300',
        ]"
        @click="focused = !focused"
      >
        <span
          :style="[settingsFontChange ? { fontFamily: placeholderText } : {}]"
          :class="[
            textXs ? 'text-xs' : 'text-base',
            placeholderBlack ? 'text-black' : 'text-truegray-300',
            !focused ? 'text-black' : 'text-truegray-300',
          ]"
          class="block truncate text-left mt-2 mr-6"
          >{{ placeholderText }}</span
        >
      </button>
      <!-- /Placeholder -->

      <!-- Dropdown Input icon right aligned -->
      <svg
        v-if="!focused"
        class="cursor-pointer z-10 h-12 w-5 absolute top-0 flex right-2"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        @click="focused = true"
      >
        <path
          fill-rule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clip-rule="evenodd"
        />
      </svg>
      <svg
        v-else
        xmlns="http://www.w3.org/2000/svg"
        class="cursor-pointer z-10 h-12 w-5 absolute top-0 flex right-2"
        viewBox="0 0 20 20"
        fill="currentColor"
        @click="focused = false"
      >
        <path
          fill-rule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          clip-rule="evenodd"
        />
      </svg>
      <!-- /Dropdown Input icon -->

      <!-- Options -->
      <ul
        ref="dropdownSelect"
        class="no-scrollbar transform ease-in-out duration-300 max-h-56 shadow-md px-6 w-full absolute left-0 z-70 bg-white text-base sm:text-sm overflow-auto focus:outline-none rounded-b-xl"
        :class="[focused ? 'top-12 opacity-1' : 'top-0 opacity-0 invisible']"
        tabindex="-1"
        role="listbox"
        aria-labelledby="listbox-label"
        aria-activedescendant="listbox-option-3"
        data-list="selectList"
      >
        <li
          v-for="(option, i) in options"
          v-if="!option.hidden || cloud"
          :id="'listbox-' + i"
          :key="i"
          :class="[
            'w-full cursor-pointer border-b last:border-transparent relative flex items-center px-2 h-12',
          ]"
          :style="[settingsFontChange ? { fontFamily: option.value } : {}]"
          role="option"
          @click="triggerSelected(option)"
        >
          <div class="flex space-x-2 items-center w-full justify-start">
            <div
              v-if="multiselect"
              class="h-3 w-3 rounded-full flex-shrink-0 border-3"
              :class="[
                isContributor ? 'border-cw-alt' : 'border-primary',
                selectedOptions.includes(option.value)
                  ? isContributor
                    ? 'bg-cw-alt'
                    : 'bg-primary'
                  : '',
              ]"
            ></div>
            <span
              :style="[settingsFontChange ? { fontFamily: option.value } : {}]"
              :class="[selectedOptions.includes(option.value)]"
              class="block truncate text-truegray-500 text-left"
              :title="getOptionText(option)"
              >{{ getOptionText(option) }}</span
            >
            <!--
              <svg v-if="multiselect && selectedOptions.includes(option.value)" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6"  fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg> 
            -->
          </div>
        </li>
      </ul>
      <!-- /Options -->
    </div>

    <!-- Options: multiselect -->
    <div
      v-if="multiselect && selectedOptions?.length"
      class="block mt-2 w-full"
    >
      <div v-for="(option, i) in options" :key="i" data-list="selectList">
        <div v-if="selectedOptions.includes(option.value)">
          <button
            type="button"
            class="mr-1 mb-1 rounded-full w-auto text-sm relative outline-none focus:outline-none border-0 flex flex-grow-0 justify-start items-stretch text-gray-500"
            :class="{ 'pointer-events-none': option.mandatory }"
            :disabled="option.mandatory"
            @click="triggerSelected(option)"
          >
            <span
              :class="[
                isContributor ? 'bg-cw-alt' : 'bg-primary',
                option.mandatory ? 'rounded-full' : 'rounded-l-full',
              ]"
              class="text-white px-2 text-xs py-1"
              >{{ getOptionText(option) }}</span
            >
            <div
              v-if="!option.mandatory"
              :style="{ marginLeft: '1px' }"
              class="text-white flex items-center justify-center rounded-r-full px-1"
              :class="isContributor ? 'bg-cw-alt' : 'bg-primary'"
            >
              <svg
                :style="{ right: '1px' }"
                xmlns="http://www.w3.org/2000/svg"
                class="h-3 w-3 relative"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </button>
        </div>
      </div>
    </div>
    <!-- /Options: multiselect -->
  </div>
</template>

<script>
import { EventBus } from '../../js/plugins/event-bus.js'
import { mapGetters } from 'vuex'
import {
  getTagAllRegionsDisplayValue,
  checkPositiveInteger,
} from '../../js/util/helper.js'

export default {
  props: {
    // data
    multiselect: { default: false },
    selectedOptionsInitial: { default: () => [] },
    options: { default: () => [] },
    label: { default: '' },

    required: {
      type: Boolean,
      default: false,
    },

    placeholderDefault: { default: '' },
    category: { default: 0 },
    responseSelectionId: { default: null },
    cloud: { default: false },
    // styling
    borderEnabled: { default: true },
    shadowEnabled: { default: true },
    marginBottom: { default: true },
    fixedWidth: { default: false },
    fixedWidthSmall: { default: false },
    fixedWidthLarge: { default: false },
    placeholderBlack: { default: false },
    textXs: { default: false },
    settingsFontChange: { default: false },

    height: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      focused: false,
      selectedOptions: [],
    }
  },

  computed: {
    ...mapGetters({
      isContributor: 'shared/isContributor',
    }),

    /**
     * Whole option object for single select;
     * null for multiselect or no option selected
     */
    selectedSingleOption() {
      if (!this.multiselect) {
        let selectedValue = this.selectedOptions[0]
        selectedValue = checkPositiveInteger(selectedValue)
          ? selectedValue
          : null
        return this.getOptionByValue(selectedValue)
      }

      return null
    },

    /**
     * Selected option text, if it exists;
     * else default option passed in props
     */
    placeholderText() {
      return this.selectedSingleOption
        ? this.getOptionText(this.selectedSingleOption)
        : this.placeholderDefault
    },
  },

  watch: {
    /**
     * When options are changed, emit the change
     * back to the parent to update component/store
     */
    selectedOptions: {
      handler: function () {
        if (this.responseSelectionId) {
          let emitValue = null

          if (this.multiselect) {
            emitValue = this.selectedOptions
          } else {
            const selectedOption = this.selectedOptions[0]
            if (checkPositiveInteger(selectedOption)) {
              emitValue = selectedOption
            }
          }

          EventBus.$emit(this.responseSelectionId, emitValue)
        }
      },

      deep: true,
    },
  },

  mounted() {
    this.$set(this, 'selectedOptions', this.selectedOptionsInitial)

    EventBus.$on('closeFilterDropdowns', () => {
      this.$set(this, 'focused', false)
    })
  },

  methods: {
    /**
     * An option's text may need to react to other changes,
     * e.g., region tag text, when the post category is changed.
     * Get the appropriate value according to the category, or
     * just default to the option's text
     *
     * @todo make getTagAllRegionsDisplayValue generic not specific to region
     * @param {object} option
     * @returns {object}
     */
    getOptionText(option) {
      const defaultText = option.text
      if (option.textOptionsByCategory) {
        return getTagAllRegionsDisplayValue(this.category) || defaultText
      }

      return defaultText
    },

    /**
     * We only track the selected option's value, not the whole
     * object. This is a getter to map back to the whole object.
     *
     * @param {number} value the option's value
     * @returns {object}
     */
    getOptionByValue(value) {
      return this.options.find((opt) => opt.value === value)
    },

    /**
     * Check if option is selected or not and update models
     * accordingly.
     *
     * If multiselect: add/remove option to/from selected
     * options array if it is not/is already selected.
     *
     * If single select: if already selected, do nothing;
     * else add to array. Also close dropdown because the user
     * has finished with their selection.
     *
     * @param {object} option: triggered option
     */
    triggerSelected(option) {
      if (option.mandatory) {
        return
      }

      const index = this.selectedOptions.indexOf(option.value)

      if (this.multiselect) {
        if (index > -1) {
          // Unselect this option
          this.selectedOptions.splice(index, 1)
        } else {
          // Select this option too
          this.selectedOptions.push(option.value)
        }
      } else {
        if (index > 0) {
          // Already selected - do nothing
        } else {
          // Remove existing option and select this one
          this.$set(this, 'selectedOptions', [])
          this.selectedOptions.push(option.value)
        }

        // Close option dropdown
        this.$set(this, 'focused', false)
      }
    },
  },
}
</script>
